import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { ListNumbers, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { STATUS_COLORS } from '../teacher/constants';

const COUNTER_COLS = ['Presente', 'Assente', 'Assente Ingiustificato', 'Insegnante Assente', 'Festivo', ''];

export function AttendanceCounterSection({ teachers, anno }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teacherId, setTeacherId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (teacherId && teacherId !== 'all') params.teacher_id = teacherId;
      if (anno) params.anno = anno;
      const res = await api.getAttendanceCounter(params);
      setData(res.data.students || []);
    } catch { toast.error('Errore caricamento contatore'); }
    finally { setLoading(false); }
  }, [teacherId, anno]);

  useEffect(() => { load(); }, [load]);

  const getTotal = (counts) => (counts['Presente'] || 0) + (counts['Assente'] || 0) + (counts['Assente Ingiustificato'] || 0);

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Statistiche Presenze</h1>
        <p className="text-[#52525B] text-sm">Riepilogo presenze per alunno con conteggio per stato</p>
      </div>
      <div className="flex items-center gap-3 mb-4 animate-fade-in animate-delay-1">
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-full sm:w-[240px] rounded-sm h-9 text-sm bg-[#0A0A0A] text-white border-[#0A0A0A] font-medium [&>svg]:text-white" data-testid="counter-teacher-filter"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli insegnanti</SelectItem>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()} - {t.instrument}</SelectItem>)}</SelectContent>
        </Select>
        {teacherId && teacherId !== 'all' && <Button variant="ghost" size="sm" onClick={() => setTeacherId('')} className="h-9 text-sm text-[#52525B]"><X size={14} className="mr-1" />Pulisci</Button>}
        <div className="text-sm text-[#52525B] font-medium ml-auto">{data.length} alunni</div>
      </div>
      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : data.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm"><ListNumbers size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Nessun dato disponibile</p></div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="attendance-counter-table">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                  <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B] sticky left-0 bg-[#FAFAFA] min-w-[180px]">Alunno</th>
                  {COUNTER_COLS.map(col => (
                    <th key={col || 'vuoto'} className={`text-center px-2 py-2 text-[10px] font-semibold uppercase tracking-wider min-w-[70px] ${STATUS_COLORS[col] || 'text-gray-600'}`}>
                      {col || 'Vuote'}
                    </th>
                  ))}
                  <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#2D2A4A] bg-[#EEF2FF] min-w-[60px]">Totale</th>
                </tr>
              </thead>
              <tbody>
                {data.map(s => (
                  <tr key={s.student_name} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                    <td className="px-3 py-1.5 text-sm font-medium uppercase sticky left-0 bg-white">{s.student_name}</td>
                    {COUNTER_COLS.map(col => {
                      const val = s.counts[col] || 0;
                      return (
                        <td key={col || 'vuoto'} className="text-center px-2 py-1.5">
                          {val > 0 ? <span className={`inline-block min-w-[28px] text-xs font-bold px-1.5 py-0.5 rounded ${STATUS_COLORS[col] || 'bg-gray-100 text-gray-700'}`}>{val}</span> : <span className="text-xs text-[#D4D4D8]">-</span>}
                        </td>
                      );
                    })}
                    <td className="text-center px-3 py-1.5"><span className="inline-block min-w-[28px] text-xs font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#2D2A4A]">{getTotal(s.counts)}</span></td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-[#FAFAFA] border-t-2 border-[#E4E4E7]">
                  <td className="px-3 py-2 text-sm font-bold uppercase sticky left-0 bg-[#FAFAFA]">TOTALE</td>
                  {COUNTER_COLS.map(col => {
                    const total = data.reduce((sum, s) => sum + (s.counts[col] || 0), 0);
                    return <td key={col || 'vuoto'} className="text-center px-2 py-2"><span className="text-xs font-bold">{total || '-'}</span></td>;
                  })}
                  <td className="text-center px-3 py-2"><span className="text-sm font-bold text-[#2D2A4A]">{data.reduce((sum, s) => sum + getTotal(s.counts), 0)}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
